import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCsrfToken } from "../services/authService";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
} from "libphonenumber-js";

import "../styles/Login.css";

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirect = searchParams.get("redirect");

  const countries = getCountries();

  const countryLabels = new Intl.DisplayNames(["es"], {
    type: "region",
  });

  const [form, setForm] = useState({
    name: "",
    lastname1: "",
    lastname2: "",
    country: "ES",
    phone: "",
    email: "",
    password: "",
    repeatPassword: "",
  });

  const [error, setError] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const selectedPrefix = `+${getCountryCallingCode(form.country)}`;

  const loginUrl = redirect
    ? `/login?redirect=${encodeURIComponent(redirect)}`
    : "/login";

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhoneChange = (e) => {
    const onlyNumbers = e.target.value.replace(/\D/g, "");

    setForm({
      ...form,
      phone: onlyNumbers,
    });
  };

  const handleCountryChange = (e) => {
    setForm({
      ...form,
      country: e.target.value,
      phone: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.password !== form.repeatPassword) {
      setError(t("register.passwordsDoNotMatch"));
      return;
    }

    const fullPhone = `${selectedPrefix}${form.phone}`;

    try {
      if (!isValidPhoneNumber(fullPhone, form.country)) {
        setError(t("register.invalidPhone"));
        return;
      }
    } catch {
      setError(t("register.invalidPhone"));
      return;
    }

    try {
      const csrf = await getCsrfToken();

      const payload = {
        ...form,
        phone: fullPhone,
      };

      delete payload.repeatPassword;

      const response = await fetch("/api/auth/register", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [csrf.headerName]: csrf.token,
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 409) {
        setError(t("register.emailExists"));
        return;
      }

      if (!response.ok) {
        setError(t("register.error"));
        return;
      }

      navigate(loginUrl, { replace: true });
    } catch {
      setError(t("register.error"));
    }
  };

  return (
    <main className="login-page">
      <section className="login-card">
        <h1>{t("register.title")}</h1>

        <p className="login-subtitle">{t("register.subtitle")}</p>

        <form onSubmit={handleSubmit} className="login-form">
          <label>
            {t("register.name")}
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />
          </label>

          <label>
            {t("register.lastname1")}
            <input
              name="lastname1"
              value={form.lastname1}
              onChange={handleChange}
              required
            />
          </label>

          <label>
            {t("register.lastname2")}
            <input
              name="lastname2"
              value={form.lastname2}
              onChange={handleChange}
            />
          </label>

          <label>
            {t("register.phone")}

            <div className="phone-row">
              <select
                name="country"
                value={form.country}
                onChange={handleCountryChange}
              >
                {countries.map((country) => (
                  <option key={country} value={country}>
                    +{getCountryCallingCode(country)}{" "}
                    {countryLabels.of(country)}
                  </option>
                ))}
              </select>

              <input
                name="phone"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.phone}
                onChange={handlePhoneChange}
                placeholder="600123123"
                required
              />
            </div>
          </label>

          <label>
            {t("register.email")}
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              required
            />
          </label>

          <label>
            {t("register.password")}

            <div className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={handleChange}
                required
              />

              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label="Mostrar contraseña"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </label>

          <label>
            {t("register.repeatPassword")}

            <div className="password-field">
              <input
                type={showRepeatPassword ? "text" : "password"}
                name="repeatPassword"
                value={form.repeatPassword}
                onChange={handleChange}
                required
              />

              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                aria-label="Mostrar contraseña"
              >
                {showRepeatPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </label>

          {error && <p className="login-error">{error}</p>}

          <button type="submit" className="login-button">
            {t("register.submit")}
          </button>
        </form>

        <div className="login-links">
          <Link to={loginUrl}>{t("register.haveAccount")}</Link>
        </div>
      </section>
    </main>
  );
}